import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSiteContent } from "@/context/SiteContext";

const DEFAULT_PHONE = "(786) 774-6692";
const DEFAULT_EMAIL = "Carlos.vindel@masterteamrealty.com";

export const Footer = () => {
  const { t } = useLanguage();
  const { content } = useSiteContent();
  const PHONE = content?.phone || DEFAULT_PHONE;
  const EMAIL = content?.email || DEFAULT_EMAIL;

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/listings", label: t.nav.listings },
    { to: "/neighborhoods", label: t.nav.neighborhoods },
    { to: "/valuation", label: t.nav.valuation },
    { to: "/testimonials", label: t.nav.testimonials },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <footer data-testid="main-footer" className="bg-ink grain-overlay relative text-cream/70">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 pb-12 border-b border-white/10">
          <div>
            <p className="font-serif text-2xl text-cream">
              Carlos <span className="italic text-gold">Vindel</span>
            </p>
            <p className="mt-3 font-serif italic text-cream/50 text-lg">{t.footer.tagline}</p>
            <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-cream/40">
              {t.footer.brokerage}{" "}
              <a data-testid="footer-brokerage-link" href="https://masterteamrealty.com" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-cream transition-colors">
                Master Team Realty
              </a>
            </p>
            <div className="mt-6 flex items-center gap-3">
              <a
                data-testid="footer-social-instagram"
                href="https://www.instagram.com/cvmiamirealtor"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                data-testid="footer-social-whatsapp"
                href="https://wa.me/17867746692"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center hover:border-gold hover:text-gold transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                </svg>
              </a>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-5">{t.footer.navigate}</p>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              {links.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="text-sm hover:text-gold transition-colors">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-5">{t.footer.contactTitle}</p>
            <ul className="space-y-3 text-sm">
              <li>
                <a data-testid="footer-phone" href={`tel:+1${PHONE.replace(/\D/g, "")}`} className="flex items-center gap-3 hover:text-gold transition-colors">
                  <Phone className="w-4 h-4 text-gold shrink-0" /> {PHONE}
                </a>
              </li>
              <li>
                <a data-testid="footer-email" href={`mailto:${EMAIL}`} className="flex items-center gap-3 hover:text-gold transition-colors">
                  <Mail className="w-4 h-4 text-gold shrink-0" /> {EMAIL}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <MapPin className="w-4 h-4 text-gold shrink-0" /> Miami, FL
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-cream/40">
          <div className="flex items-center gap-4">
            <img src="/images/realtor-logo.png" alt="REALTOR®" data-testid="footer-realtor-logo" className="h-10 w-auto invert" />
            <p data-testid="footer-license">{t.footer.license}</p>
          </div>
          <p>© {new Date().getFullYear()} Carlos Vindel Realty. {t.footer.rights} · <Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-gold transition-colors">Admin</Link></p>
        </div>
      </div>
    </footer>
  );
};
